import React from 'react';
import {
  TextField,
  Box,
  Grid,
} from '@mui/material';

import { FormDialog } from '../FormDialog';
import type { Organization } from '../../api/config';

interface OrganizationFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Partial<Organization>) => void;
  initialData?: Organization;
  error?: string | null;
  isPending?: boolean;
}

export const OrganizationForm: React.FC<OrganizationFormProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
  error,
  isPending,
}) => {
  const [formData, setFormData] = React.useState<Partial<Organization>>({
    name: '',
    short_name: '',
    primary_contact_name: '',
    primary_contact_email: '',
    primary_contact_phone: '',
  });

  React.useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name,
        short_name: initialData.short_name,
        primary_contact_name: initialData.primary_contact_name || '',
        primary_contact_email: initialData.primary_contact_email || '',
        primary_contact_phone: initialData.primary_contact_phone || '',
      });
    } else {
      setFormData({
        name: '',
        short_name: '',
        primary_contact_name: '',
        primary_contact_email: '',
        primary_contact_phone: '',
      });
    }
  }, [initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleClose = () => {
    setFormData({
      name: '',
      short_name: '',
      primary_contact_name: '',
      primary_contact_email: '',
      primary_contact_phone: '',
    });
    onClose();
  };

  return (
    <FormDialog
      open={open}
      onClose={handleClose}
      onSubmit={handleFormSubmit}
      title={initialData ? 'Edit Organization' : 'Create Organization'}
      submitLabel={initialData ? 'Update' : 'Create'}
      error={error}
      isPending={isPending}
    >
      <Box sx={{ pt: 1 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              name="name"
              label="Organization Name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="short_name"
              label="Short Name"
              value={formData.short_name}
              onChange={handleChange}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="primary_contact_name"
              label="Primary Contact Name"
              value={formData.primary_contact_name}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="primary_contact_email"
              label="Primary Contact Email"
              type="email"
              value={formData.primary_contact_email}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="primary_contact_phone"
              label="Primary Contact Phone"
              value={formData.primary_contact_phone}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
        </Grid>
      </Box>
    </FormDialog>
  );
};
